import { CloseOutlined, MenuOutlined } from "@ant-design/icons";
import { UserMenu } from "@components/molecules";
import { CartDrawer } from "@components/organisms";
import { useAuth } from "@hooks";
import { CartProvider } from "@contexts";
import { LinkedinLogo, User, YoutubeLogo } from "@phosphor-icons/react";
import { Button, Skeleton } from "antd";
import { useEffect, useRef, useState } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";

// Inner component that uses cart context
const PublicTemplateInner = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);
  const { profile: { data: profile, isLoading } } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrolled(currentScrollY > 50);

      // Do not hide the header while the mobile menu is open
      if (mobileMenuOpen) {
        setHeaderVisible(true);
        lastScrollY.current = currentScrollY;
        return;
      }

      if (currentScrollY > lastScrollY.current && currentScrollY > 50) {
        setHeaderVisible(false); // Scroll para baixo
      } else {
        setHeaderVisible(true); // Scroll para cima
      }
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [mobileMenuOpen]); // Added mobileMenuOpen as a dependency

  const navigateWithScroll = (path: string) => {
    navigate(path);
    window.scrollTo(0, 0);
    setMobileMenuOpen(false); // Fecha o menu ao navegar
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    // Force the header to stay visible when the menu opens
    if (!mobileMenuOpen) {
      setHeaderVisible(true);
    }
  };

  const menuLinks = [
    { path: "/", label: "HOME" },
    { path: "/about", label: "ABOUT" },
    { path: "/join", label: "CAREERS" },
    ...(profile?.profile === 'admin' ? [{ path: "/merch", label: "SHOP" }] : []),
    { path: "/faq", label: "FAQ" },
    { path: "/contact", label: "CONTACT" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-dark overflow-x-hidden">
      <header
        className={`bg-charcoal fixed top-0 z-50 w-full transition-all duration-300 border-b-2
        ${scrolled ? "shadow-lg" : ""}
        ${headerVisible ? "translate-y-0 border-primary" : "-translate-y-full border-transparent"}`}
        style={{ isolation: 'isolate' }}
      >
        <div className="container h-20 flex items-center justify-between mx-auto px-4" style={{ position: 'relative', zIndex: 1 }}>
          {/* Logo Section */}
          <div
            className="flex items-center cursor-pointer flex-shrink-0 relative z-10"
            onClick={() => navigateWithScroll("/")}
          >
            <img
              src="/assets/images/estabrook-logo.png"
              alt="Estabrook Inc"
              className={`h-20 sm:h-24 md:h-26 lg:h-32 ${headerVisible ? "translate-y-1/4" : "-translate-y-1/4"} duration-300`}
              style={{ backfaceVisibility: 'hidden', willChange: 'transform' }}
            />
          </div>

          {/* Menu Desktop */}
          <nav className="hidden lg:flex text-sm xl:text-base gap-4 xl:gap-8 header-nav-links">
            {menuLinks.map((link) => (
              <Link key={link.path} to={link.path} onClick={() => window.scrollTo(0, 0)}>
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop actions */}
          <div className="hidden lg:flex items-center gap-3 xl:gap-5 flex-shrink-0">
            {isLoading ? (
              <Skeleton.Avatar active size="small" />
            ) : profile ? (
              <UserMenu profile={profile} />
            ) : (
              <div
                className="bg-primary !h-9 xl:!h-10 cursor-pointer btn-icon-cut-top-left p-2 flex items-center justify-center"
                onClick={() => navigateWithScroll("/login")}
                aria-label="Login"
              >
                <User size={20} weight="bold" color="black" />
              </div>
            )}
            <Button
              className="btn-single-cut-dark-text !text-sm xl:!text-base !h-9 xl:!h-10 !px-4 xl:!px-6"
              onClick={() => navigateWithScroll("/")}
            >
              GET A QUOTE
            </Button>
          </div>

          {/* Mobile/tablet actions (hamburger menu and login) */}
          <div className="flex lg:hidden items-center gap-2 sm:gap-3">
            {isLoading ? (
              <Skeleton.Avatar active size="small" />
            ) : profile ? (
              <UserMenu profile={profile} />
            ) : (
              <div
                className="bg-primary !h-9 cursor-pointer btn-icon-cut-top-left p-1.5 flex items-center justify-center"
                onClick={() => navigateWithScroll("/login")}
                aria-label="Login"
              >
                <User size={18} weight="bold" color="black" />
              </div>
            )}
            <button
              className="text-white text-xl sm:text-2xl z-20 p-1"
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <CloseOutlined /> : <MenuOutlined />}
            </button>
          </div>
        </div>

        {/* Painel do Menu Mobile/Tablet */}
        <div
          className={`lg:hidden w-full absolute top-full left-0 bg-charcoal shadow-lg transition-transform duration-500 ease-in-out
            ${mobileMenuOpen ? "translate-y-0" : "-translate-y-[150%]"}`}
        >
          <div className="flex flex-col p-4">
            {menuLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="font-azonix flex justify-end text-sm uppercase py-3 px-2 border-b border-rock-grey text-white hover:text-primary transition-colors"
                onClick={() => navigateWithScroll(link.path)}
              >
                {link.label}
              </Link>
            ))}
            {/* Get a Quote button in mobile menu */}
            <div className="mt-4 flex justify-end">
              <Button
                className="btn-single-cut-dark-text !text-sm !h-10"
                onClick={() => navigateWithScroll("/")}
              >
                GET A QUOTE
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 pt-20">
        <Outlet />
      </main>

      {/* Cart Drawer */}
      <CartDrawer />

      <footer className="bg-black text-secondary footer-container">
        <div className="container mx-auto px-4 py-8">
          {/* Layout: mobile (single column), iPad (logo on top + 2 columns), desktop (3 columns) */}
          <div className="flex flex-col gap-8 md:gap-12 lg:grid lg:grid-cols-3 lg:gap-12">
            {/* Logo and socials - always first, centered on top for mobile/md, first column on lg */}
            <div className="flex flex-col items-center lg:items-start">
              <img
                src="/assets/images/estabrook-E-logo.png"
                alt="Estabrook Inc. Logo"
                className="w-30 md:w-50 h-auto my-6 cursor-pointer"
                onClick={() => navigateWithScroll("/")}
              />
              <div className="flex space-x-8 justify-center footer-social-icons">
                <a href="https://www.youtube.com/@estabrookinc" target="_blank" rel="noopener noreferrer">
                  <YoutubeLogo size={26} />
                </a>
                <a href="https://www.linkedin.com/company/estabrook-inc" target="_blank" rel="noopener noreferrer">
                  <LinkedinLogo size={26} />
                </a>
              </div>
            </div>
            
            {/* Quick Links and Contact Us - single column up to 600px, 2 columns from 600px to lg, separate columns at lg */}
            <div className="footer-links-grid">
              {/* Quick Links */}
              <div className="ml-6 my-6 md:ml-0 lg:ml-15 xl:ml-20">
                <h3 className="font-azonix text-white text-lg mb-4">QUICK LINKS</h3>
                <ul className="space-y-2 footer-links">
                  <li><Link to="/" onClick={() => window.scrollTo(0, 0)}>Home</Link></li>
                  <li><Link to="/about" onClick={() => window.scrollTo(0, 0)}>About</Link></li>
                  <li><Link to="/join" onClick={() => window.scrollTo(0, 0)}>Careers</Link></li>
                  {profile?.profile === 'admin' && (
                    <li><Link to="/merch" onClick={() => window.scrollTo(0, 0)}>Shop</Link></li>
                  )}
                  <li><Link to="/faq" onClick={() => window.scrollTo(0, 0)}>FAQ</Link></li>
                  <li><Link to="/contact" onClick={() => window.scrollTo(0, 0)}>Contact</Link></li>
                </ul>
              </div>

              {/* Contact Us */}
              <div className="ml-6 my-6 md:ml-0 lg:ml-0 xl:ml-10">
                <h3 className="font-azonix text-white text-lg mb-4">CONTACT US</h3>
                <ul className="space-y-5 footer-links">
                  <li className="flex items-start text-light">
                    <img src="/assets/icons/location-icon.svg" alt="Location icon" className="h-7 mr-2 pt-1" />
                    <span>PO Box 1924<br />Truckee, 96160</span>
                  </li>
                  <li className="flex items-center">
                    <img src="/assets/icons/phone-icon.svg" alt="Phone icon" className="h-7 mr-2 pb-1" />
                    <a href="tel:+18889585509">+1 (888) 958-5509</a>
                  </li>
                  <li className="flex items-center">
                    <img src="/assets/icons/mail-icon.svg" alt="Mail icon" className="h-8 mr-2 py-1" />
                    <a href="mailto:contact@estabrookinc.com">contact@estabrookinc.com</a>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <hr className="border-gray-700 my-8 md:my-10" />

          <div className="flex flex-col items-center">
            <p className="text-gray-400 text-sm mb-4">
              © {new Date().getFullYear()} Estabrook Inc. All rights reserved.
            </p>
            {/* <div className="flex justify-center space-x-4 md:space-x-6">
              <Link to='/' onClick={() => window.scrollTo(0, 0)} className='text-secondary text-sm'>Privacy Policy</Link>
              <Link to='/' onClick={() => window.scrollTo(0, 0)} className='text-secondary text-sm'>Terms of Service</Link>
              <Link to='/' onClick={() => window.scrollTo(0, 0)} className='text-secondary text-sm'>Sitemap</Link>
            </div> */}
          </div>
        </div>
      </footer>
    </div>
  );
};

// Main component wrapped with CartProvider
export const PublicTemplate = () => {
  return (
    <CartProvider>
      <PublicTemplateInner />
    </CartProvider>
  );
};
